import React from "react";
import { Snackbar, Alert } from "@mui/material";
import type { AlertColor } from "@mui/material";
import { getNotificationIconColor } from "../../utils/notificationUtils";

export interface NotificationSnackbarProps {
  /** The message to display. If null/undefined, snackbar is hidden */
  message: string | null;
  /** Called when the snackbar should be closed */
  onClose: () => void;
  /** Severity/type of the notification */
  severity?: AlertColor;
  /** Duration before auto-hide in milliseconds */
  autoHideDuration?: number;
  /** Position of the snackbar */
  anchorOrigin?: {
    vertical: "top" | "bottom";
    horizontal: "left" | "center" | "right";
  };
  /** Whether to show the close button */
  showCloseButton?: boolean;
}

/**
 * Reusable notification snackbar component with consistent styling and behavior.
 * Centralized styling: dark background (#323232) with colored icons based on severity.
 *
 * Usage:
 * <NotificationSnackbar
 *   message={successMessage}
 *   onClose={() => setSuccessMessage(null)}
 *   severity="success"
 * />
 */
export function NotificationSnackbar({
  message,
  onClose,
  severity = "success",
  autoHideDuration = 6000,
  anchorOrigin = { vertical: "bottom", horizontal: "center" },
  showCloseButton = true,
}: NotificationSnackbarProps) {
  return (
    <Snackbar
      open={!!message}
      autoHideDuration={autoHideDuration}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
    >
      <Alert
        onClose={showCloseButton ? onClose : undefined}
        severity={severity}
        variant="filled"
        sx={{
          width: "100%",
          backgroundColor: "#323232",
          color: "white",
          "& .MuiAlert-icon": {
            color: getNotificationIconColor(severity),
          },
        }}
      >
        {message}
      </Alert>
    </Snackbar>
  );
}

/**
 * Hook for managing notification state with the NotificationSnackbar component.
 * Provides consistent state management for success/error notifications.
 */
export function useNotification() {
  const [successMessage, setSuccessMessage] = React.useState<string | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const showSuccess = React.useCallback((message: string) => {
    setSuccessMessage(message);
  }, []);

  const showError = React.useCallback((message: string) => {
    setErrorMessage(message);
  }, []);

  const clearSuccess = React.useCallback(() => {
    setSuccessMessage(null);
  }, []);

  const clearError = React.useCallback(() => {
    setErrorMessage(null);
  }, []);

  const clearAll = React.useCallback(() => {
    setSuccessMessage(null);
    setErrorMessage(null);
  }, []);

  return {
    successMessage,
    errorMessage,
    showSuccess,
    showError,
    clearSuccess,
    clearError,
    clearAll,
  };
}
